import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { Save, Calendar, Info, CheckCircle2, AlertTriangle } from 'lucide-react';
import { getActiveProfile } from '../../config/arizona-sped';

const profile = getActiveProfile();
const DISABILITY_CATEGORIES = profile.disabilityCategories;

/** Next working day after a date string (skip Sat/Sun) */
function nextWorkingDay(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  const day = d.getDay();
  if (day === 0) d.setDate(d.getDate() + 1); // Sunday → Monday
  if (day === 6) d.setDate(d.getDate() + 2); // Saturday → Monday
  return d.toISOString().split('T')[0];
}

/** One year minus one day from a date string */
function oneYearMinusOne(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

function findCategory(value) {
  if (!value) return null;
  return DISABILITY_CATEGORIES.find(c => c.label === value || c.id === value) || null;
}

function mergeContent(existing) {
  const defaults = {
    parent_guardian: { name: '', relationship: '', phone: '', email: '', address: '' },
    school_name: '', district_name: '',
    dates: { meeting_date: '', begin_date: '', end_date: '', reevaluation_due: '' },
    eligibility: { category: '', subcategories: [], determination_date: '' },
    case_manager: '', notes: '',
  };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.parent_guardian && !existing.eligibility) {
    return { ...defaults, notes: existing.notes };
  }
  const merged = {
    ...defaults,
    ...existing,
    parent_guardian: { ...defaults.parent_guardian, ...(existing.parent_guardian || {}) },
    dates: { ...defaults.dates, ...(existing.dates || {}) },
    eligibility: { ...defaults.eligibility, ...(existing.eligibility || {}) },
  };
  // Backward compat
  if (existing.dates?.iep_effective && !merged.dates.begin_date) merged.dates.begin_date = existing.dates.iep_effective;
  if (existing.dates?.iep_end && !merged.dates.end_date) merged.dates.end_date = existing.dates.iep_end;
  if (existing.dates?.reevaluation && !merged.dates.reevaluation_due) merged.dates.reevaluation_due = existing.dates.reevaluation;
  // Migrate old primary_disability → eligibility.category
  if (existing.primary_disability && !merged.eligibility.category) merged.eligibility.category = existing.primary_disability;
  if (!Array.isArray(merged.eligibility.subcategories)) merged.eligibility.subcategories = [];
  return merged;
}

export default function CoverSheetEditor({ section, caseData, onSave, saving }) {
  const student = caseData?.student || {};
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const endDateManual = useRef(false);

  useEffect(() => {
    setContent(mergeContent(section?.content));
    endDateManual.current = false;
  }, [section?.section_id]);

  const updateField = (key, value) => setContent(prev => ({ ...prev, [key]: value }));
  const updateNested = (parent, key, value) => setContent(prev => ({ ...prev, [parent]: { ...prev[parent], [key]: value } }));
  const handleSave = () => onSave(content);

  // Smart date: when meeting date changes, auto-set begin + end
  const handleMeetingDateChange = (val) => {
    setContent(prev => {
      const newDates = { ...prev.dates, meeting_date: val };
      if (val) {
        const begin = nextWorkingDay(val);
        newDates.begin_date = begin;
        if (!endDateManual.current) {
          newDates.end_date = oneYearMinusOne(begin);
        }
      }
      return { ...prev, dates: newDates };
    });
  };

  // Smart date: when begin date changes manually, auto-update end (unless user overrode end)
  const handleBeginDateChange = (val) => {
    setContent(prev => {
      const newDates = { ...prev.dates, begin_date: val };
      if (val && !endDateManual.current) {
        newDates.end_date = oneYearMinusOne(val);
      }
      return { ...prev, dates: newDates };
    });
  };

  const handleEndDateChange = (val) => {
    endDateManual.current = true;
    updateNested('dates', 'end_date', val);
  };

  // Eligibility category change → clear subcategories
  const handleEligibilityCategoryChange = (val) => {
    setContent(prev => ({ ...prev, eligibility: { ...prev.eligibility, category: val, subcategories: [] } }));
  };

  const toggleSubcategory = (label) => {
    setContent(prev => {
      const arr = prev.eligibility.subcategories || [];
      const next = arr.includes(label) ? arr.filter(x => x !== label) : [...arr, label];
      return { ...prev, eligibility: { ...prev.eligibility, subcategories: next } };
    });
  };

  const carriedFrom = section?.content?._carried_from;
  const eligCat = findCategory(content.eligibility?.category);
  const hasSubcats = eligCat?.subcategories?.length > 0;
  const selectedSubs = content.eligibility?.subcategories || [];
  const rule = eligCat?.rules;
  const belowMin = rule?.minRequired && selectedSubs.length < rule.minRequired;

  return (
    <div className="max-w-4xl space-y-6" data-testid="cover-sheet-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="cover-sheet-title">Student Data / Cover Sheet</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          {carriedFrom && (
            <Badge className="mt-2 bg-blue-50 text-blue-700 text-[10px] border border-blue-200">
              Carried forward from prior case
            </Badge>
          )}
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl shrink-0" data-testid="cover-sheet-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Student Info */}
      <div className="bg-[#F3F4F1] border border-[#E5E5E5] rounded-2xl p-5" data-testid="student-info-card">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-3">Student Information (from shared registry)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div><span className="text-xs text-[#595959]">Name</span><p className="font-medium text-[#1C1C1C]">{student.first_name} {student.last_name}</p></div>
          <div><span className="text-xs text-[#595959]">Grade</span><p className="font-medium text-[#1C1C1C]">{student.grade || '\u2014'}</p></div>
          <div><span className="text-xs text-[#595959]">Student ID</span><p className="font-medium text-[#1C1C1C]">{student.student_id?.slice(-8) || '\u2014'}</p></div>
          <div><span className="text-xs text-[#595959]">Case Type</span><p className="font-medium text-[#1C1C1C] capitalize">{caseData?.case_subtype || '\u2014'}</p></div>
        </div>
      </div>

      {/* IEP Meeting & Period Dates — smart auto-populate */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="iep-dates-section">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-4 w-4 text-[#3B6A9B]" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">IEP Meeting & Period</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">IEP Meeting Date</label>
            <Input type="date" value={content.dates.meeting_date || ''} onChange={e => handleMeetingDateChange(e.target.value)} className="rounded-xl text-sm h-9" data-testid="date-meeting" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Begin Date</label>
            <Input type="date" value={content.dates.begin_date || ''} onChange={e => handleBeginDateChange(e.target.value)} className="rounded-xl text-sm h-9" data-testid="date-begin" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">End Date</label>
            <Input type="date" value={content.dates.end_date || ''} onChange={e => handleEndDateChange(e.target.value)} className="rounded-xl text-sm h-9" data-testid="date-end" />
          </div>
        </div>
        <p className="text-[10px] text-[#595959] mt-2 flex items-center gap-1">
          <Info className="h-3 w-3" />Begin Date defaults to the next working day after meeting. End Date defaults to one year minus one day.
        </p>
      </div>

      {/* Eligibility & Reevaluation Cycle — with subcategory support */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="eligibility-cycle-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">Current Eligibility</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Current Eligibility Category</label>
            <Select value={content.eligibility?.category || undefined} onValueChange={handleEligibilityCategoryChange}>
              <SelectTrigger className="rounded-xl text-sm h-9" data-testid="eligibility-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {DISABILITY_CATEGORIES.map(c => (
                  <SelectItem key={c.id} value={c.label}>
                    {c.label}
                    {c.note && <span className="text-[10px] text-[#595959] ml-1">({c.note})</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Eligibility Determination Date</label>
            <Input type="date" value={content.eligibility?.determination_date || ''} onChange={e => updateNested('eligibility', 'determination_date', e.target.value)} className="rounded-xl text-sm h-9" data-testid="eligibility-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Reevaluation Due Date</label>
            <Input type="date" value={content.dates?.reevaluation_due || ''} onChange={e => updateNested('dates', 'reevaluation_due', e.target.value)} className="rounded-xl text-sm h-9" data-testid="date-reeval-due" />
            <p className="text-[10px] text-[#595959] mt-1">3-year eligibility cycle</p>
          </div>
        </div>

        {/* Multi-select subcategories for SLD/DD/PSD */}
        {hasSubcats && (
          <div className="mt-4 bg-[#F3F4F1]/50 border border-[#E5E5E5] rounded-xl p-4" data-testid="eligibility-subcategories">
            <div className="flex items-center gap-2 mb-2.5">
              <label className="text-xs font-medium text-[#1C1C1C]">
                Eligibility Areas
                <span className="text-[10px] text-[#595959] font-normal ml-1">({eligCat.label})</span>
              </label>
              {eligCat.selectionMode === 'multi' && <Badge className="text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B]">Multi-select</Badge>}
              {selectedSubs.length > 0 && <Badge className="text-[10px] bg-[#5A8A5A]/10 text-[#5A8A5A]">{selectedSubs.length} selected</Badge>}
            </div>
            {rule?.advisory && (
              <p className={`text-[10px] mb-2.5 ${belowMin ? 'text-amber-600 font-medium' : 'text-[#595959]'}`}>
                {belowMin && <AlertTriangle className="h-3 w-3 inline mr-1 -mt-0.5" />}
                {rule.advisory}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {eligCat.subcategories.map(sub => {
                const active = selectedSubs.includes(sub.label);
                return (
                  <button key={sub.id} type="button" onClick={() => toggleSubcategory(sub.label)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      active ? 'bg-[#5A8A5A] text-white border-[#5A8A5A]' : 'bg-white text-[#595959] border-[#E5E5E5] hover:border-[#5A8A5A]/40 hover:bg-[#5A8A5A]/5'
                    }`} data-testid={`elig-sub-${sub.id}`}>
                    {active && <CheckCircle2 className="h-3 w-3 inline mr-1 -mt-0.5" />}{sub.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Parent/Guardian */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="parent-guardian-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">Parent / Guardian Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[['name', 'Name', 'Full name'], ['relationship', 'Relationship', 'e.g., Mother, Father, Guardian'], ['phone', 'Phone', 'Phone number'], ['email', 'Email', 'Email address']].map(([key, label, ph]) => (
            <div key={key}>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">{label}</label>
              <Input value={content.parent_guardian[key] || ''} onChange={e => updateNested('parent_guardian', key, e.target.value)} placeholder={ph} className="rounded-xl text-sm h-9" data-testid={`parent-${key}`} />
            </div>
          ))}
        </div>
        <div className="mt-4">
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Address</label>
          <Input value={content.parent_guardian.address || ''} onChange={e => updateNested('parent_guardian', 'address', e.target.value)} placeholder="Full mailing address" className="rounded-xl text-sm h-9" data-testid="parent-address" />
        </div>
      </div>

      {/* School/District */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="school-district-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">School / District</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">School Name</label>
            <Input value={content.school_name || ''} onChange={e => updateField('school_name', e.target.value)} placeholder="School name" className="rounded-xl text-sm h-9" data-testid="school-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">District Name</label>
            <Input value={content.district_name || ''} onChange={e => updateField('district_name', e.target.value)} placeholder="District name" className="rounded-xl text-sm h-9" data-testid="district-name" />
          </div>
        </div>
      </div>

      {/* Case Manager + Notes */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="case-manager-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Case Manager</label>
            <Input value={content.case_manager || ''} onChange={e => updateField('case_manager', e.target.value)} placeholder="Case manager name" className="rounded-xl text-sm h-9" data-testid="case-manager" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Additional Notes</label>
          <Textarea value={content.notes || ''} onChange={e => updateField('notes', e.target.value)} placeholder="Any additional cover sheet notes..." rows={3} className="rounded-xl text-sm" data-testid="cover-sheet-notes" />
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="cover-sheet-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Cover Sheet'}
        </Button>
      </div>
    </div>
  );
}
